import { useState, useCallback } from 'react'
import type { Page, CreatePageInput, UpdatePageInput } from '../model/contracts'

const STORAGE_KEY = 'omni-sight-pages'

function loadPages(): Page[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function savePages(pages: Page[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pages))
}

export function usePagesController() {
  const [pages, setPages] = useState<Page[]>(loadPages)

  const createPage = useCallback(
    (input: CreatePageInput) => {
      const newPage: Page = {
        id: crypto.randomUUID(),
        ...input,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      const updated = [...pages, newPage]
      setPages(updated)
      savePages(updated)
    },
    [pages],
  )

  const updatePage = useCallback(
    (input: UpdatePageInput) => {
      const updated = pages.map((page) =>
        page.id === input.id
          ? { ...page, ...input, updatedAt: new Date().toISOString() }
          : page,
      )
      setPages(updated)
      savePages(updated)
    },
    [pages],
  )

  const deletePage = useCallback(
    (id: string) => {
      const updated = pages.filter((page) => page.id !== id)
      setPages(updated)
      savePages(updated)
    },
    [pages],
  )

  return {
    pages,
    createPage,
    updatePage,
    deletePage,
  }
}
